import { retryRestClient } from "api/restClient";
import { Buffer } from "buffer";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Client for the interactive, retryable import-session backend
 * (`operator-task-import-improvements-interactive-problems-modal-and-retry`).
 *
 * Flow: stage the zip ONCE (`POST /import/session`) -> `preflight` dry-run that
 * collects per-record problems without committing -> the user picks resolutions
 * / config -> `apply` (always async; the result is written back onto the
 * `import_session` record) -> poll that record until it reaches a terminal
 * status, reading the structured result. The zip is never re-uploaded between
 * retries; each apply re-runs against the already-staged session.
 *
 * API calls live in this hook rather than a saga because the flow is an
 * interactive, component-owned state machine (stage -> review -> apply -> poll
 * -> retry) with no cross-screen Redux state to share; the result surface is
 * read by polling the session record over REST (the shipped `import_session`
 * resource is a user-subrecord that is not wired into the GraphQL live
 * subscription machinery, so REST polling is the robust v1 — see the task for
 * the optional live-subscription follow-up).
 */

/** A single problem the import would hit, as emitted by the preflight/apply. */
export interface ImportProblem {
    /** Taxonomy code, e.g. "schedule_enabled_locked", "missing_reference". */
    code: string;
    record_type: string | null;
    record_name: string | null;
    message: string;
    severity: "blocking" | "warning";
    /**
     * Offered resolutions. A `change-value` resolution names the `field` whose
     * value the user replaces (e.g. `name` when the name is already taken).
     */
    resolutions: { id: string; label: string; field?: string }[];
    /**
     * Present when the problem is on a sub-record (e.g. an object template
     * attribute): the record it belongs to, for display.
     */
    parent_record_type?: string | null;
    parent_record_name?: string | null;
}

/** One record the import would create/update/skip. */
export interface ImportRecordRow {
    record_type: string;
    name: string | null;
    action: "create" | "update" | "skip";
    status: "ok" | "problem";
    /**
     * Present only on PREFLIGHT rows that are a referenced-only LOCATION
     * (project/bundle) the import would create/restore via "Create missing
     * target locations": the backend emits such a location as its own preview
     * row (action "create", status "ok") so the user sees it before applying,
     * tagging a trashed-then-restored location restored:true so the Action cell
     * reads "restore" (matching the apply done view). Absent on ordinary rows.
     */
    restored?: boolean;
}

/** The structured preflight / apply result surface. */
export interface ImportPreview {
    records: ImportRecordRow[];
    problems: ImportProblem[];
    summary: { ok: number; warning: number; blocking: number; total: number };
    /** Present only on a successful apply: imported ids grouped by record type. */
    imported?: Record<string, string[]>;
    /**
     * Present only on a successful apply: target locations (project/bundle) that
     * did not exist on the target (or were in the trash) and were provided by
     * "Create missing target locations". The done view lists each as its own row,
     * named — distinct from an imported record (Margot, 2026-10-20). A location
     * that was absent is created and shown "Created"; a location that was
     * soft-deleted is restored (un-trashed) and shown "Restored" (restored: true).
     */
    created_locations?: {
        record_type: string;
        name: string | null;
        restored?: boolean;
    }[];
    /**
     * Present on a preflight: every project/bundle the export refers to, keyed
     * by its SOURCE name, with the name it resolves to under the current remap.
     * Drives the "Import into" remap table.
     */
    source_locations?: ImportSourceLocation[];
    /**
     * Present only on a successful apply: the fields the user replaced with a
     * "Set a value" (change-value) resolution. `from`/`to` are given for `name`
     * only (another field may carry a secret).
     */
    overridden?: ImportOverride[];
    /**
     * Present only on a successful apply: the enabled records a
     * "disable-reenable" resolution wrote disabled and then enabled again.
     */
    disable_reenabled?: ImportDisableReenabled[];
}

/** A record written by a disable-reenable resolution (see ImportPreview.disable_reenabled). */
export interface ImportDisableReenabled {
    record_type: string;
    record_name: string | null;
}

/** One field replaced by a change-value resolution (see ImportPreview.overridden). */
export interface ImportOverride {
    record_type: string;
    record_name: string | null;
    field: string;
    from?: unknown;
    to?: unknown;
}

/** A project/bundle the export refers to (see ImportPreview.source_locations). */
export interface ImportSourceLocation {
    record_type: string;
    name: string;
    target_name: string;
}

/** A per-problem resolution choice stored on the session between retries. */
export interface ImportResolution {
    record_type: string | null;
    record_name: string | null;
    resolution: string;
    /** change-value only: the field to replace and the value to import. */
    field?: string;
    value?: unknown;
}

/** Session-level config applied on each attempt. */
export interface ImportSessionConfig {
    allow_upsert?: boolean;
    create_missing_locations?: boolean;
    /** Reference remap: record type -> source name (or "*") -> target name. */
    remap?: Record<string, Record<string, string>>;
}

export type ImportPhase =
    | "idle"
    | "staging"
    | "preflighting"
    | "review"
    | "applying"
    | "done"
    | "error";

export interface StagedFile {
    name: string;
    /** Raw file content as a binary string (use-file-picker readAs BinaryString). */
    content: string;
}

const POLL_INTERVAL_MS = 1500;

/** Stable cache key for a session config (key order does not matter). */
export const configKey = (config: ImportSessionConfig): string => {
    const sortDeep = (value: unknown): unknown => {
        if (Array.isArray(value)) {
            return value.map(sortDeep);
        }
        if (value && typeof value === "object") {
            return Object.keys(value as Record<string, unknown>)
                .sort()
                .reduce<Record<string, unknown>>((acc, k) => {
                    acc[k] = sortDeep((value as Record<string, unknown>)[k]);
                    return acc;
                }, {});
        }
        return value;
    };
    return JSON.stringify(sortDeep(config));
};

/**
 * Unwrap an import_session record from a REST GET response body.
 *
 * The staging/preflight/apply steps hit hand-written custom endpoints that
 * return a FLAT body ({id, status, ...}). The result poll, however, reads the
 * session through the generic CRUD route (`add_crud_table`), whose GET wraps
 * the record under an `import_session` key ({import_session: {...}}). Reading
 * `data.status` straight off that envelope yields undefined, so the poll would
 * never observe the terminal `applied`/`failed` status and the modal would hang
 * on "Importing…" forever even though the backend finished. Unwrap the envelope
 * (falling back to the flat shape) so the poll sees the real status/result.
 */
export const unwrapSession = (data: any): any =>
    (data && data.import_session) ?? data;

/**
 * Shown when a request against the staged session returns 404: the session
 * expired (the reaper deleted it) or never existed, so the only way forward is
 * to stage the file again.
 */
export const SESSION_EXPIRED_MESSAGE =
    "This import session has expired. Upload the file again to continue.";

export const extractError = (error: any): string =>
    (error?.response?.status === 404 ? SESSION_EXPIRED_MESSAGE : "") ||
    error?.response?.data?.message ||
    error?.response?.data?.title ||
    error?.message ||
    "Unexpected error";

export const useImportSession = () => {
    const [phase, setPhase] = useState<ImportPhase>("idle");
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [fileName, setFileName] = useState<string>("");
    const [preview, setPreview] = useState<ImportPreview | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [hasApplied, setHasApplied] = useState<boolean>(false);
    // True while a config-driven re-preflight is in flight (keeps the review UI
    // mounted — see refreshPreflight).
    const [refreshing, setRefreshing] = useState<boolean>(false);
    const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Preflight results per config already checked in this session, so going
    // back to an earlier choice (e.g. project A -> B -> A) shows its result at
    // once instead of checking again. Cleared when a new file is staged.
    const preflightCacheRef = useRef<Map<string, ImportPreview>>(new Map());
    // Only the newest re-check may set the preview: a slow earlier check must
    // not overwrite the result for the choice the user made after it.
    const refreshSeqRef = useRef(0);

    const clearPoll = useCallback(() => {
        if (pollRef.current) {
            clearTimeout(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    useEffect(() => () => clearPoll(), [clearPoll]);

    const reset = useCallback(() => {
        clearPoll();
        setPhase("idle");
        setSessionId(null);
        setFileName("");
        setPreview(null);
        setError(null);
        setHasApplied(false);
        setRefreshing(false);
        preflightCacheRef.current = new Map();
        refreshSeqRef.current += 1;
    }, [clearPoll]);

    /**
     * Persist the chosen config on the session and re-run the preflight dry-run,
     * so the preview reflects the config the user just changed. The backend
     * preflight reads the session's stored config (create_missing_locations /
     * allow_upsert / remap) — without this re-run, toggling "create missing
     * target locations" visibly does nothing even though it would change the
     * outcome on apply (reported by Margot, 2026-10-08). Stays in the `review`
     * phase and exposes a transient `refreshing` flag so the review UI (preview +
     * config checkboxes) is not torn down and re-mounted on every toggle.
     */
    const refreshPreflight = useCallback(
        async (config: ImportSessionConfig) => {
            if (!sessionId) {
                return;
            }
            const seq = ++refreshSeqRef.current;
            const key = configKey(config);
            const cached = preflightCacheRef.current.get(key);
            setError(null);
            if (cached) {
                // Same config as an earlier check: reuse its result. Apply sends
                // the config again, so the session's stored config is not needed
                // to match here.
                setPreview(cached);
                setRefreshing(false);
                return;
            }
            setRefreshing(true);
            try {
                await retryRestClient.patch(`/import/session/${sessionId}`, {
                    config,
                });
                const preflightResponse = await retryRestClient.post(
                    `/import/session/${sessionId}/preflight`
                );
                preflightCacheRef.current.set(key, preflightResponse.data);
                if (seq === refreshSeqRef.current) {
                    setPreview(preflightResponse.data);
                }
            } catch (caught) {
                if (seq === refreshSeqRef.current) {
                    setError(extractError(caught));
                }
            } finally {
                if (seq === refreshSeqRef.current) {
                    setRefreshing(false);
                }
            }
        },
        [sessionId]
    );

    /** Stage the zip once, then run the preflight dry-run. */
    const stageAndPreflight = useCallback(async (file: StagedFile) => {
        clearPoll();
        preflightCacheRef.current = new Map();
        refreshSeqRef.current += 1;
        setError(null);
        setPreview(null);
        setSessionId(null);
        setHasApplied(false);
        setFileName(file.name);
        setPhase("staging");
        try {
            const body = Buffer.from(file.content, "binary");
            const stageResponse = await retryRestClient.post(
                `/import/session?file_name=${encodeURIComponent(file.name)}`,
                body,
                {
                    headers: { "Content-Type": "application/octet-stream" },
                    maxBodyLength: Infinity,
                    maxContentLength: Infinity,
                }
            );
            const id: string = stageResponse.data.id;
            setSessionId(id);
            setPhase("preflighting");
            const preflightResponse = await retryRestClient.post(
                `/import/session/${id}/preflight`
            );
            setPreview(preflightResponse.data);
            setPhase("review");
        } catch (caught) {
            setError(extractError(caught));
            setPhase("error");
        }
    }, [clearPoll]);

    /** Poll the session record until it reaches a terminal status. */
    const pollResult = useCallback((id: string) => {
        const tick = async () => {
            try {
                const response = await retryRestClient.get(`/import/session/${id}`);
                const session = unwrapSession(response.data);
                const status: string = session.status;
                if (status === "applied") {
                    setPreview(session.result ?? null);
                    setPhase("done");
                    return;
                }
                if (status === "failed") {
                    // Apply refused (unresolved blocking problems) or errored:
                    // surface the shrunken result and let the user resolve + retry.
                    setPreview(session.result ?? null);
                    setPhase("review");
                    return;
                }
                pollRef.current = setTimeout(tick, POLL_INTERVAL_MS);
            } catch (caught) {
                setError(extractError(caught));
                setPhase("error");
            }
        };
        pollRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    }, []);

    /**
     * Persist the chosen config + resolutions on the session, then re-apply the
     * already-staged zip and poll for the result.
     */
    const applySession = useCallback(
        async (config: ImportSessionConfig, resolutions: ImportResolution[]) => {
            if (!sessionId) {
                return;
            }
            clearPoll();
            setError(null);
            setHasApplied(true);
            setPhase("applying");
            try {
                await retryRestClient.patch(`/import/session/${sessionId}`, {
                    config,
                    resolutions,
                });
                await retryRestClient.post(`/import/session/${sessionId}/apply`);
                pollResult(sessionId);
            } catch (caught) {
                setError(extractError(caught));
                setPhase("review");
            }
        },
        [sessionId, clearPoll, pollResult]
    );

    return {
        phase,
        sessionId,
        fileName,
        preview,
        error,
        hasApplied,
        refreshing,
        stageAndPreflight,
        refreshPreflight,
        applySession,
        reset,
    };
};
